import { api, ApiError } from "./client";

export interface AdminOrg {
  id: string;
  slug: string | null;
  name: string;
  status: string;
  is_registered: boolean;
}

export interface AdminDevice {
  talis_auth_id: string;
  serial_number: string;
  model: string;
  org_id: string | null;
  is_setup: boolean;
  last_seen_at: string | null;
}

// The admin endpoints return HTTP 428 when kraken has no `auth`-audience
// session yet. Bounce the browser through the silent OIDC-for-auth flow,
// returning to the current page.
const AUTH_SESSION_REQUIRED = 428;

function authStart(): void {
  const next = encodeURIComponent(window.location.pathname + window.location.search);
  window.location.href = `/sso/auth-start?next=${next}`;
}

async function withAuthSession<T>(fn: () => Promise<T>): Promise<T> {
  try {
    return await fn();
  } catch (e) {
    if (e instanceof ApiError && e.status === AUTH_SESSION_REQUIRED) {
      authStart();
    }
    throw e;
  }
}

export const fetchAdminOrgs = () =>
  withAuthSession(() => api.get<{ orgs: AdminOrg[] }>("/api/admin/orgs"));

export const syncNow = () =>
  withAuthSession(() =>
    api.post<{ orgs_synced: number; devices_synced: number }>("/api/admin/orgs/sync"),
  );

export const registerOrg = (org_id: string) =>
  withAuthSession(() => api.post("/api/admin/orgs/register", { org_id }));

export const createOrg = (slug: string, name: string, description?: string) =>
  withAuthSession(() =>
    api.post<{ status: string; id: string }>("/api/admin/orgs/create", {
      slug,
      name,
      description,
    }),
  );

export const fetchAdminDevices = () =>
  withAuthSession(() => api.get<{ devices: AdminDevice[] }>("/api/admin/devices"));

export const assignDevice = (talis_auth_id: string, org_id: string | null) =>
  withAuthSession(() =>
    api.post(`/api/admin/devices/${talis_auth_id}/assign`, { org_id }),
  );
